import type { ScreenName, ScreenParams, TabStack } from '@/types/navigation';

type Tab = keyof TabStack;

/**
 * URL targets for every screen in the legacy stack-nav vocabulary.
 * The adapter at lib/useUrlScreenNavigation.ts consults this map when
 * a migrated screen calls `navigation.pushScreen(...)` /
 * `navigation.navigate(...)` / `navigation.changeTab(...)`.
 *
 * Conventions:
 *  - Every entry takes the optional `params` arg the legacy callers
 *    already pass (`{ caseId, userId, hashName, code, ...}`); missing
 *    ids resolve to `''` and produce an obviously-wrong URL — this
 *    surfaces the bug at the call site instead of silently 404'ing.
 *  - `case-item` historically takes `(caseId, hashName)` — both encoded
 *    in the path so the resulting URL is shareable.
 *  - `inventory-item` carries a UUID; we keep it under /inventory/.
 *  - `profile` doubles as own-profile and public-profile; presence of
 *    `userId` switches the URL.
 *  - Screens whose target page does not yet exist still resolve here
 *    so unblocking is just creating the page; the legacy /app
 *    catch-all is the fallback for completely-unknown screen names.
 */
export const SCREEN_URL_MAP: Record<ScreenName, (params?: ScreenParams) => string> = {
  // Balance tab
  balance: () => '/',
  packages: () => '/packages',
  payment: () => '/payment',
  'draws-history': () => '/draws',
  'draw-participants': (p) => `/draws/${encodeURIComponent(String(p?.drawId ?? ''))}`,

  // Games tab
  games: () => '/games',
  'rush-mid': () => '/games/rush-mid',
  'rush-mid-match-detail': (p) => `/games/rush-mid/match/${encodeURIComponent(String(p?.matchId ?? ''))}`,
  'rush-mid-open-game': (p) => `/games/rush-mid/game/${encodeURIComponent(String(p?.gameId ?? ''))}`,
  'rush-mid-spectate': (p) => `/games/rush-mid/spectate/${encodeURIComponent(String(p?.matchId ?? ''))}`,
  'rush-mid-replay': (p) => `/games/rush-mid/replay/${encodeURIComponent(String(p?.matchId ?? ''))}`,
  'rush-mid-raffle-round': (p) => `/games/rush-mid/raffle/${encodeURIComponent(String(p?.roundId ?? ''))}`,
  'rush-mid-raffle-winners': () => '/games/rush-mid/raffle/winners',
  'rush-mid-survey': () => '/games/rush-mid-survey',
  roulette: () => '/games/roulette',
  'roulette-match': (p) => `/games/roulette/match/${encodeURIComponent(String(p?.matchId ?? ''))}`,
  cases: () => '/cases',
  'case-details': (p) => `/cases/${encodeURIComponent(String(p?.caseId ?? ''))}`,
  'case-item': (p) =>
    `/cases/${encodeURIComponent(String(p?.caseId ?? ''))}/items/${encodeURIComponent(String(p?.hashName ?? ''))}`,
  'case-roulette': (p) => `/cases/${encodeURIComponent(String(p?.caseId ?? ''))}/open`,
  'case-honeycomb': (p) =>
    `/cases/${encodeURIComponent(String(p?.caseId ?? ''))}/open/multi?count=${encodeURIComponent(String(p?.count ?? 2))}`,
  'case-open-results': (p) => {
    // The drop ids ride the query string (comma-joined) — the reverse map
    // strips the query, so the /results path stays unambiguous. The id list
    // is what makes the result list restorable after diving into an item.
    const ids = (p?.dropIds as string[] | undefined) ?? [];
    return `/cases/${encodeURIComponent(String(p?.caseId ?? ''))}/results?ids=${encodeURIComponent(ids.join(','))}`;
  },
  auctions: (p) => (p?.filter ? `/auctions?filter=${encodeURIComponent(String(p.filter))}` : '/auctions'),
  'auction-details': (p) => `/auctions/${encodeURIComponent(String(p?.auctionId ?? ''))}`,
  shop: () => '/shop',
  'shop-skins': (p) => {
    // ShopCategoriesScreen pushes 'shop-skins' with { weapon, category,
    // weaponImage } when the user picks a weapon tile. The screen needs
    // `weapon` (and optionally `category`) to fetch — without it the
    // query stays disabled and renders "Скины не найдены".
    const qs: string[] = [];
    for (const k of ['weapon', 'category', 'weaponImage'] as const) {
      const v = p?.[k];
      if (v !== undefined && v !== null && v !== '') {
        qs.push(`${k}=${encodeURIComponent(String(v))}`);
      }
    }
    return `/shop/skins${qs.length ? `?${qs.join('&')}` : ''}`;
  },
  'shop-items': (p) => {
    // ShopCategoriesScreen has two distinct flows that both push
    // 'shop-items' with different param shapes:
    //   handleOtherClick → { type, title }   (gear/other categories)
    //   filters apply    → { category, weapon, skinName, type, ... }
    // Use category when present, fall back to type so the URL has a
    // distinct path segment in either case. Other filter values go
    // into the query string.
    const segment = p?.category ?? p?.categoryId ?? p?.type ?? 'all';
    const qs: string[] = [];
    for (const k of ['weapon', 'skinName', 'type'] as const) {
      const v = p?.[k];
      if (v !== undefined && v !== null && v !== '' && k !== 'type') {
        qs.push(`${k}=${encodeURIComponent(String(v))}`);
      } else if (k === 'type' && v && v !== segment) {
        qs.push(`${k}=${encodeURIComponent(String(v))}`);
      }
    }
    return `/shop/${encodeURIComponent(String(segment))}${qs.length ? `?${qs.join('&')}` : ''}`;
  },
  'shop-item': (p) => {
    // ShopItem (services/generated/Api.ts) is keyed by `hashName`, not `id`.
    // Category lives at `item.category` (string) — also not `categoryId`.
    // For non-skin items (crates, stickers, agents…) `item.category` is
    // null; fall back to `item.type` so the URL has a real segment
    // ("/shop/crate/Dreams Case" instead of "/shop/unknown/...").
    const item = p?.item;
    const hashName = item?.hashName ?? p?.hashName ?? '';
    const segment = item?.category ?? p?.category ?? item?.type ?? p?.type ?? 'unknown';
    return `/shop/${encodeURIComponent(String(segment))}/${encodeURIComponent(String(hashName))}`;
  },
  upgrade: () => '/upgrade',
  'case-battles': () => '/case-battles',
  'case-battle-create': () => '/case-battles/create',
  'case-battle-details': (p) => `/case-battles/${encodeURIComponent(String(p?.battleId ?? ''))}`,
  'case-battle-history': () => '/case-battles/history',
  'giveaway-details': (p) => `/giveaways/${encodeURIComponent(String(p?.giveawayId ?? ''))}`,
  'daily-giveaway-details': (p) => `/daily-giveaways/${encodeURIComponent(String(p?.giveawayId ?? ''))}`,
  'daily-giveaway-details-winners': (p) =>
    `/daily-giveaways/${encodeURIComponent(String(p?.giveawayId ?? ''))}/winners`,

  // Inventory tab
  inventory: (p) => (p?.filter ? `/inventory?filter=${encodeURIComponent(String(p.filter))}` : '/inventory'),
  'inventory-item': (p) => `/inventory/${encodeURIComponent(String(p?.itemId ?? ''))}`,
  'send-item': (p) => `/inventory/${encodeURIComponent(String(p?.itemId ?? ''))}/send`,

  // Contracts tab
  contracts: () => '/contracts',

  // Referrals tab
  referrals: () => '/referrals',

  // Profile tab
  profile: (p) => (p?.userId ? `/profile/${encodeURIComponent(String(p.userId))}` : '/profile'),
  achievements: () => '/achievements',
  settings: () => '/settings',
  'trade-link': () => '/settings/trade-link',
  promocode: (p) => (p?.code ? `/promocode?code=${encodeURIComponent(String(p.code))}` : '/promocode'),
  wheel: () => '/wheel',
  language: () => '/settings/language',
  'level-info': () => '/help/level',
  'xp-history': () => '/xp/history',
  'recruiter-guide': () => '/help/recruiter',

  // Onboarding
  onboarding: () => '/onboarding/welcome',
  'onboarding-item-reveal': () => '/onboarding/item-reveal',
  'onboarding-tasks': () => '/onboarding/tasks',
  'onboarding-skin-decision': () => '/onboarding/skin-decision',
  'onboarding-friends': () => '/onboarding/friends',
  'onboarding-bonus': () => '/onboarding/bonus',
};

export const TAB_URL_MAP: Record<Tab, string> = {
  balance: '/home',
  cases: '/cases',
  games: '/games',
  inventory: '/inventory',
  referrals: '/referrals',
  profile: '/profile',
  shop: '/shop',
  onboarding: '/onboarding/welcome',
};

/**
 * URL prefix → owning tab, in priority order. First match wins, so put
 * deeper / more-specific paths before their parents. Used by the
 * adapter to compute `state.activeTab` from `usePathname()` for
 * tab-bar highlighting and for `getCurrentScreen` heuristics.
 */
const TAB_BY_PREFIX: ReadonlyArray<readonly [string, Tab]> = [
  ['/onboarding', 'onboarding'],
  ['/home', 'balance'],
  ['/cases', 'cases'],
  ['/games', 'games'],
  ['/auctions', 'games'],
  ['/case-battles', 'games'],
  ['/daily-giveaways', 'games'],
  ['/giveaways', 'games'],
  ['/upgrade', 'games'],
  ['/shop', 'shop'],
  ['/inventory', 'inventory'],
  ['/contracts', 'games'],
  ['/referrals', 'referrals'],
  ['/profile', 'profile'],
  ['/settings', 'profile'],
  ['/promocode', 'profile'],
  ['/wheel', 'profile'],
  ['/xp', 'profile'],
  ['/help', 'profile'],
  ['/achievements', 'profile'],
  ['/payment', 'balance'],
  ['/packages', 'balance'],
  ['/draws', 'balance'],
];

export function pathnameToTab(pathname: string): Tab {
  for (const [prefix, tab] of TAB_BY_PREFIX) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return tab;
  }
  return 'balance';
}

/**
 * Best-effort reverse: a pathname → ScreenName. Used by adapter's
 * `getCurrentScreen()` so unmigrated callers reading
 * `navigation.state.stacks[activeTab]` see something sensible.
 *
 * Returns `null` when the URL doesn't match any known screen — caller
 * should fall back to the tab's root screen.
 */
/**
 * Reverse mapping built once at module load by walking SCREEN_URL_MAP
 * with a probe params object. Builders that don't read params produce
 * a stable URL; that URL's path becomes an EXACT entry. Builders that
 * read params produce a URL containing the probe sentinel — those go
 * into a regex list with the sentinel positions replaced by capture
 * groups. Hand-maintained reverse map drifted on every URL-schema
 * change; auto-deriving keeps the two sides in sync.
 *
 * NOTE: order matters for the regex list — longer/more-specific paths
 * must beat their parents. We sort regex entries by descending
 * specificity (more `[^/]+` placeholders + longer literal prefix wins).
 */
const PROBE_VALUES: ScreenParams = {
  caseId: '__caseId__',
  hashName: '__hashName__',
  itemId: '__itemId__',
  drawId: '__drawId__',
  auctionId: '__auctionId__',
  battleId: '__battleId__',
  giveawayId: '__giveawayId__',
  partnerId: '__partnerId__',
  category: '__category__',
  weapon: '__weapon__',
  type: '__type__',
  skinName: '__skinName__',
  userId: '__userId__',
  code: '__code__',
  filter: '__filter__',
};

const PROBE_TOKEN_RE = /__[a-zA-Z]+__/g;

interface ReverseEntry {
  re: RegExp;
  screen: ScreenName;
}

const { exact: EXACT_REVERSE, dynamic: DYNAMIC_REVERSE } = ((): {
  exact: Record<string, ScreenName>;
  dynamic: ReverseEntry[];
} => {
  const exact: Record<string, ScreenName> = {};
  const dynamic: ReverseEntry[] = [];

  for (const [screen, builder] of Object.entries(SCREEN_URL_MAP) as Array<
    [ScreenName, (p?: ScreenParams) => string]
  >) {
    let probed: string;
    try {
      probed = builder(PROBE_VALUES);
    } catch {
      continue;
    }
    // Strip query — reverse map only cares about pathname.
    const pathname = probed.split('?')[0];
    if (!PROBE_TOKEN_RE.test(pathname)) {
      // Static URL — first writer wins (matches old hand-maintained order).
      if (!exact[pathname]) exact[pathname] = screen;
      continue;
    }
    PROBE_TOKEN_RE.lastIndex = 0;
    // Encode tokens may have been encoded by builders (encodeURIComponent
    // on '__caseId__' yields '__caseId__' — safe in this charset).
    const pattern =
      '^' + pathname.replace(PROBE_TOKEN_RE, '[^/]+').replace(/\//g, '\\/') + '$';
    dynamic.push({ re: new RegExp(pattern), screen });
  }

  // Sort dynamic regexes by descending specificity so /cases/[id]/open
  // is checked before /cases/[id]. Heuristic: longer source = more
  // specific.
  dynamic.sort((a, b) => b.re.source.length - a.re.source.length);

  return { exact, dynamic };
})();

export function pathnameToScreen(pathname: string): ScreenName | null {
  if (EXACT_REVERSE[pathname]) return EXACT_REVERSE[pathname];
  for (const { re, screen } of DYNAMIC_REVERSE) {
    if (re.test(pathname)) return screen;
  }
  return null;
}

/**
 * Real page.tsx routes that don't have a SCREEN_URL_MAP entry — auth
 * callback landings, OAuth-flow steps, and similar dead-end pages
 * that callers don't `pushScreen()` to. The back-button hierarchy
 * walk needs to know these exist so it doesn't skip them and land
 * the user on the active tab root with no in-between context.
 */
const ORPHAN_KNOWN_PATHS = new Set<string>([
  '/login',
  '/login/connect',
  '/login/link-telegram',
  '/login/steam-linked',
  '/login/telegram-linked',
  '/payment/success',
  '/payment/fail',
  '/exchange',
]);

/**
 * True when `pathname` corresponds to a real, navigable page in the
 * app — either via SCREEN_URL_MAP or via the orphan list. Used by
 * the back-button hierarchy walk to skip "phantom" parents like
 * `/onboarding` or `/help` (no page.tsx, no SCREEN_URL_MAP entry)
 * and prefer a real ancestor or the active tab root.
 */
export function isKnownScreenPath(pathname: string): boolean {
  if (ORPHAN_KNOWN_PATHS.has(pathname)) return true;
  return pathnameToScreen(pathname) !== null;
}

/**
 * Resolves a screen+params to a target URL. Falls back to the legacy
 * /app surface when an unknown screen name is requested — keeps
 * behaviour predictable while screens are partially migrated.
 */
export function buildScreenUrl(screen: ScreenName, params?: ScreenParams): string {
  const builder = SCREEN_URL_MAP[screen];
  if (builder) return builder(params);
  if (process.env.NODE_ENV !== 'production') {
    console.warn(`[screen-url-map] no URL mapping for screen "${screen}", falling back to /app`);
  }
  return '/app';
}
